/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Divider, Stack, Typography } from '@mui/material';
import type { FlowRunStatus } from '@poweralpha/common';
import { Fragment } from 'react';
import { formatAllDone, formatPercent } from './progress';
import { SimRunRow } from './SimRunRow';
import { getPanelSx, sectionLabelSx, tabularSx } from './simProgressStyles';
import type { useSimulationProgress } from './useSimulationProgress';

type Props = Pick<
  ReturnType<typeof useSimulationProgress>,
  'overall' | 'runningRuns' | 'queuedRuns' | 'recentRuns' | 'progressById' | 'fractionById'
> & {
  canOpen: (run: FlowRunStatus) => boolean;
  onOpen: (run: FlowRunStatus) => void;
};

/** Popover listing the user's running, queued and recent flow runs with their progress. */
export function SimProgressPanel({
  overall,
  runningRuns,
  queuedRuns,
  recentRuns,
  progressById,
  fractionById,
  canOpen,
  onOpen,
}: Readonly<Props>) {
  const sections = [
    { label: `Running · ${runningRuns.length}`, runs: runningRuns },
    { label: `Queued · ${queuedRuns.length}`, runs: queuedRuns },
    { label: 'Recent', runs: recentRuns },
  ].filter((section) => section.runs.length);
  const allDone = formatAllDone(
    runningRuns.map((run) => ({
      startTime: run.start_time,
      fraction: fractionById.get(run.id) ?? null,
    })),
  );

  return (
    <Box role="dialog" aria-label="Simulations" sx={getPanelSx}>
      <Box sx={{ px: 2, pt: 2, pb: 1.75 }}>
        <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Simulations</Typography>
          {runningRuns.length > 0 && overall !== null && (
            <Typography sx={{ ...tabularSx, fontSize: 20, fontWeight: 600 }}>
              {formatPercent(overall)}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>
            {runningRuns.length || queuedRuns.length
              ? [
                  `${runningRuns.length} running`,
                  queuedRuns.length ? `${queuedRuns.length} queued` : null,
                  runningRuns.length > 1 ? 'weighted by runs' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Nothing running'}
          </Typography>
          {allDone && (
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{allDone}</Typography>
          )}
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ overflowY: 'auto', px: 0.5, pb: 1 }}>
        {sections.length ? (
          sections.map((section) => (
            <Fragment key={section.label}>
              <Typography sx={sectionLabelSx}>{section.label}</Typography>
              {section.runs.map((run) => (
                <SimRunRow
                  key={run.id}
                  run={run}
                  progress={progressById.get(run.id)}
                  fraction={fractionById.get(run.id) ?? null}
                  canOpen={canOpen(run)}
                  onOpen={onOpen}
                />
              ))}
            </Fragment>
          ))
        ) : (
          <Typography sx={{ px: 2, py: 3, fontSize: 12, color: 'text.secondary' }}>
            No simulations in the last 14 days.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
